import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { clearRestoreCache } from "@/lib/restore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

// Works for albums ({ album }) and artists ({ artist: name }).
export const CoverFinder = ({ album, artist, open, onOpenChange, onApplied }) => {
  const [cands, setCands] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const isArtist = !album && !!artist;
  const subject = isArtist ? artist : album?.name;
  const key = isArtist ? artist : album?.id;

  const search = async (query = "") => {
    setCands(null);
    try {
      const { data } = isArtist
        ? await api.get("/artwork/artist/candidates", { params: { name: artist, q: query } })
        : await api.get(`/artwork/${album.id}/candidates`, { params: { q: query } });
      setCands(data.candidates);
    } catch (e) { toast.error(formatApiError(e)); setCands([]); }
  };
  useEffect(() => { if (open && key) { setQ(""); search(); } }, [open, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (fn, label) => {
    setBusy(label);
    try {
      const { data } = await fn();
      clearRestoreCache();
      toast.success(isArtist ? `Photo saved for ${artist}` : `Cover saved for ${album.name}`);
      onApplied?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const pick = (c) => apply(() => (isArtist ? api.post("/artwork/artist", { name: artist, url: c.url }) : api.post(`/artwork/${album.id}`, { url: c.url })), c.url);
  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (isArtist) fd.append("name", artist);
    apply(() => api.post(isArtist ? "/artwork/artist/upload" : `/artwork/${album.id}/upload`, fd), "upload");
  };

  if (!key) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[min(94vw,52rem)] max-w-none overflow-y-auto" data-testid="cover-finder-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isArtist ? "Photo for" : "Album art for"} “{subject}”</DialogTitle>
          {!isArtist && <p className="text-sm text-zinc-500">{album.artist}{album.year ? ` · ${album.year}` : ""}</p>}
        </DialogHeader>
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); search(q); }}>
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isArtist ? "Search a different artist name…" : "Search a different album name…"} className="h-10 rounded-full pl-9" data-testid="cover-finder-query" />
          </div>
          <button type="submit" className="rounded-full bg-zinc-900 px-4 py-2 text-sm text-white" data-testid="cover-finder-search">Search</button>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={!!busy} className="flex items-center gap-2 rounded-full border border-zinc-300 px-4 py-2 text-sm" data-testid="cover-finder-upload">
            {busy === "upload" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}Upload image
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="cover-finder-file-input" />
        </form>
        {cands === null ? (
          <p className="flex items-center gap-2 py-10 text-sm text-zinc-500" data-testid="cover-finder-loading"><Loader2 size={14} className="animate-spin" />Searching iTunes and Deezer…</p>
        ) : cands.length === 0 ? (
          <p className="py-10 text-center text-sm text-zinc-500" data-testid="cover-finder-empty">{isArtist ? "No photos found" : "No covers found"} — try another spelling above, or upload an image.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4" data-testid="cover-finder-results">
            {cands.map((c, i) => (
              <button key={c.url} type="button" onClick={() => pick(c)} disabled={!!busy}
                className="group text-left transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid={`cover-candidate-${i}`}>
                <div className={`relative aspect-square overflow-hidden bg-zinc-100 shadow-sm ${isArtist ? "rounded-full" : "rounded-md"}`}>
                  <img src={c.thumb || c.url} alt={c.album || c.artist} className="h-full w-full object-cover" loading="lazy" />
                  {busy === c.url && <div className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 size={18} className="animate-spin" /></div>}
                  <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">{c.source}</span>
                </div>
                <p className="mt-2 truncate text-xs font-medium text-zinc-900">{c.album || c.artist}</p>
                <p className="truncate text-[11px] text-zinc-500">{c.album ? c.artist : "Artist photo"}{c.year ? ` · ${c.year}` : ""}</p>
              </button>
            ))}
          </div>
        )}
        <p className="flex items-center gap-1.5 text-[11px] text-zinc-400"><ImagePlus size={11} />{isArtist ? "Photos" : "Covers"} are saved into Resonance's own storage and shown everywhere in your library.</p>
      </DialogContent>
    </Dialog>
  );
};
